import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, IconButton, Box, Divider, CircularProgress, Button, Chip, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PauseIcon from "@mui/icons-material/Pause";
import { collection, doc, getDoc, updateDoc, onSnapshot, Timestamp, deleteDoc, writeBatch } from "firebase/firestore";
import { db } from "../../../../Config/firebase";
import { useAppSelector } from "../../../../state/state";
import { selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2";

// Updated Notification type to support different request types
type Notification = {
  id: string;
  ReadRequest: boolean;
  RequestDate: Timestamp;
  RequestType: "SettlementReport" | "Pause";
  requestParams: SettlementReportParams | PauseRequestParams;
};

// Type for Settlement Report params
type SettlementReportParams = {
  ReportDate: string;
};

// Type for Pause Request params
type PauseRequestParams = {
  LiveSubscriptionID: string;
  LiveSubscriptionRef: string;
  subscriptionPlanRef: string;
  studentName: string;
  subscriptionPlan: string;
  RequestedMonth?: string;
};

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  }) + " " + date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true
  });
}

export default function NotificationsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const serviceProviderId = useAppSelector(selectEffectiveUID);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<"All" | "Unread">("All");
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState<boolean>(false);

  useEffect(() => {
    if (serviceProviderId) {
      const unsubscribe = fetchNotifications(serviceProviderId);
      return () => unsubscribe();
    }
  }, [serviceProviderId]);

  const fetchNotifications = (spId: string) => {
    setLoading(true);
    const pendingRequestsRef = collection(db, "ServiceProviders", spId, "pendingRequests");

    const unsubscribe = onSnapshot(
      pendingRequestsRef,
      (querySnapshot) => {
        const fetchedNotifications: Notification[] = [];

        querySnapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();

          // Handle different types of notifications
          let notificationData: Notification | null = null;

          switch (data.RequestType) {
            case "SettlementReport":
              notificationData = {
                id: docSnap.id,
                ReadRequest: data.ReadRequest ?? false,
                RequestDate: data.RequestDate ?? Timestamp.fromDate(new Date()),
                RequestType: "SettlementReport",
                requestParams: {
                  ReportDate: data.requestParams?.ReportDate ?? "Unknown Date"
                }
              };
              break;

            case "Pause":
              // Initialize requestParams with fallback values
              const requestParams = {
                LiveSubscriptionID: data.requestParams?.LiveSubscriptionID ?? "",
                LiveSubscriptionRef: data.requestParams?.LiveSubscriptionRef ?? "",
                subscriptionPlanRef: data.requestParams?.subscriptionPlanRef ?? "",
                studentName: data.requestParams?.studentName ?? "Unknown Student",
                subscriptionPlan: data.requestParams?.subscriptionPlan ?? "Unknown Plan",
                RequestedMonth: data.requestParams?.RequestedMonth ?? ""
              };

              // Only add Pause notification if it has required references
              if (requestParams.LiveSubscriptionRef && requestParams.subscriptionPlanRef) {
                notificationData = {
                  id: docSnap.id,
                  ReadRequest: data.ReadRequest ?? false,
                  RequestDate: data.RequestDate ?? Timestamp.fromDate(new Date()),
                  RequestType: "Pause",
                  requestParams
                };
              }
              break;
          }

          if (notificationData) {
            fetchedNotifications.push(notificationData);
          }
        });

        // Limit to the 15 most recent notifications
        const limitedNotifications = fetchedNotifications.slice(0, 15);

        setNotifications(limitedNotifications);
        setUnreadCount(limitedNotifications.filter((notif) => !notif.ReadRequest).length);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  };

  const handleMarkAllAsRead = async () => {
    if (!serviceProviderId || !notifications.length) return;

    setIsMarkingAllAsRead(true);
    try {
      const batch = writeBatch(db);

      // Only mark unread notifications as read
      const unreadNotifications = notifications.filter(notif => !notif.ReadRequest);

      if (unreadNotifications.length === 0) {
        setIsMarkingAllAsRead(false);
        return;
      }

      unreadNotifications.forEach(notification => {
        const notificationRef = doc(db, "ServiceProviders", serviceProviderId, "pendingRequests", notification.id);
        batch.update(notificationRef, { ReadRequest: true });
      });

      await batch.commit();

      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, ReadRequest: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  const handleDeleteNotification = async (event: React.MouseEvent, notificationId: string) => {
    // Stop event propagation to prevent navigation when delete is clicked
    event.stopPropagation();

    if (!serviceProviderId) return;

    try {
      await deleteDoc(doc(db, "ServiceProviders", serviceProviderId, "pendingRequests", notificationId));

      // Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));

      // Update unread count if necessary
      const deletedNotification = notifications.find(notif => notif.id === notificationId);
      if (deletedNotification && !deletedNotification.ReadRequest) {
        setUnreadCount(prev => Math.max(prev - 1, 0));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    try {
      // Mark notification as read if it's not already
      if (!notification.ReadRequest) {
        const notificationRef = doc(db, "ServiceProviders", serviceProviderId, "pendingRequests", notification.id);
        await updateDoc(notificationRef, { ReadRequest: true });

        // Update local state
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notification.id ? { ...notif, ReadRequest: true } : notif
          )
        );

        // Update unread count
        setUnreadCount(prev => prev - 1);

      }

      // Handle different types of notifications
      switch (notification.RequestType) {
        case "SettlementReport":
          const reportParams = notification.requestParams as SettlementReportParams;

          try {
            // Parse report date
            const reportDate = new Date(reportParams.ReportDate);
            if (isNaN(reportDate.getTime())) {
              throw new Error('Invalid report date');
            }

            // Get today's date
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize to midnight

            const reportDay = new Date(reportDate);
            reportDay.setHours(0, 0, 0, 0); // Normalize to midnight

            // Check if report date is today
            if (reportDay.getTime() === today.getTime()) {
              // If today, navigate to Recent Transactions tab with today filter
              navigate("/statistics?tab=recent&filter=today");
            } else {
              // If not today, navigate to Recent Transactions tab with week filter
              navigate("/statistics?tab=recent&filter=week");
            }
          } catch (error) {
            console.error('Error processing report date:', error);
            navigate("/statistics");
          }
          break;

        case "Pause":
          const pauseParams = notification.requestParams as PauseRequestParams;
          const { subscriptionPlanRef, LiveSubscriptionID } = pauseParams;

          if (!subscriptionPlanRef || !LiveSubscriptionID) {
            console.error("Missing required references in the notification");
            return;
          }

          try {
            if (typeof subscriptionPlanRef === 'string') {
              const pathParts = subscriptionPlanRef.split('/');
              const subscriptionId = pathParts[pathParts.length - 1];

              navigate(`/mysubscriptions/subscription/${subscriptionId}?livesubid=${LiveSubscriptionID}`);
            } else {
              const subscriptionDoc = await getDoc(subscriptionPlanRef);

              if (!subscriptionDoc.exists()) {
                console.error("Subscription plan not found");
                return;
              }
              navigate(`/mysubscriptions/subscription/${subscriptionDoc.id}?livesubid=${LiveSubscriptionID}`);
            }
          } catch (error) {
            console.error("Error fetching subscription document:", error);
          }
          break;
      }
      onClose();
    } catch (error) {
      console.error("Error handling notification click:", error);
    }
  };

  const filteredNotifications = notifications
    .filter((notification) => (filter === "All" ? true : !notification.ReadRequest))
    .sort((a, b) => b.RequestDate.toMillis() - a.RequestDate.toMillis())
    .slice(0, 15);

  // Function to generate action link based on notification type
  const getActionLink = (notificationType: string) => {
    return notificationType === "SettlementReport" ? "Check it out now" : "Review Now";
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: 650, p: 2, marginRight: '10px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={400}>Notifications </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" gap={1}>
              <Chip
                label="All"
                color={filter === "All" ? "primary" : "default"}
                onClick={() => setFilter("All")}
                sx={{ borderRadius: 4 }}
              />
              <Chip
                label="Unread"
                color={filter === "Unread" ? "primary" : "default"}
                onClick={() => setFilter("Unread")}
                sx={{ borderRadius: 4 }}
              />
            </Box>

            <Button
              variant="text"
              color="primary"
              size="medium"
              sx={{ textTransform: 'none', fontWeight: 'normal' }}
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllAsRead}
            >
              Mark All as Read
            </Button>
          </Box>
          <List
            sx={{
              maxHeight: '80vh',
              overflowY: 'auto',
              borderRadius: '8px',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              gap: 2,
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress />
              </Box>
            ) : filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 0,
                    px: 0,
                    mr: 1,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    borderBottom: "1px solid #eee",
                    backgroundColor: notification.ReadRequest ? "transparent" : "#e0e0e0",
                    position: 'relative',
                    mb: 0.8
                  }}
                >
                  <ListItemAvatar sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Avatar sx={{
                      width: 40,
                      height: 40,
                      bgcolor: '#e8f1ff',
                      color: '#4285F4',
                      marginRight: '10px'
                    }}>
                      {notification.RequestType === "SettlementReport"
                        ? <AssignmentIcon style={{ color: '#4285F4' }} />
                        : <PauseIcon style={{ color: '#4285F4' }} />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      notification.RequestType === "SettlementReport" ?
                        <Typography variant="body1" sx={{ fontWeight: 400, mt: 0.5, fontSize: '0.950rem' }}>
                          Your Settlement Report for {(notification.requestParams as SettlementReportParams).ReportDate} is ready!
                        </Typography> :
                        <Typography variant="body1" sx={{ fontWeight: 400, mt: 0.5, fontSize: '0.950rem' }}>
                          Pause Request from {(notification.requestParams as PauseRequestParams).studentName} for the {(notification.requestParams as PauseRequestParams).subscriptionPlan} for the month of {(notification.requestParams as PauseRequestParams).RequestedMonth}
                        </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="#4285F4"
                          sx={{ display: 'block', mt: 0.5, fontSize: '0.875rem' }}
                        >
                          {getActionLink(notification.RequestType)}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.5, fontSize: '0.875rem' }}
                        >
                          {formatDate(notification.RequestDate.toDate())}
                        </Typography>
                      </>
                    }
                  />
                  {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Delete Notification">
                      <IconButton
                        size="small"
                        aria-label="delete"
                        onClick={(e) => handleDeleteNotification(e, notification.id)}
                        sx={{ 
                          opacity: 0.7,
                          marginLeft: 'auto',
                          '&:hover': {
                            opacity: 1,
                            color: 'red',
                          },
                          color: 'red' 
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box> */}
                </ListItem>
              ))
            ) : (
              <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
                No Notifications to display.
              </Typography>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}